import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Switch,
  Space, Popconfirm, message, Card, Tag, Upload,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DeleteFilled } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from '../api/announcements';
import { getBranches } from '../api/branches';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import useAuthStore from '../store/authStore';
import api from '../api/axios';
import { apiErrorMessage } from '../utils/apiError';

const { Option } = Select;

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'color',
  'list', 'bullet', 'link',
];

const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const Announcements = () => {
  const { user } = useAuthStore();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const navigate = useNavigate();

  const [announcements, setAnnouncements] = useState([]);
  const [branches, setBranches]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [modalOpen, setModalOpen]         = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [search, setSearch]               = useState('');
  const [branchFilter, setBranchFilter]   = useState(null);
  const [activeFilter, setActiveFilter]   = useState(null);
  const [bodyHtml, setBodyHtml]           = useState('');
  const [imageUrl, setImageUrl]           = useState('');
  // imagePath: kaydedilecek deger. undefined = kullanici gorseli hic
  // degistirmedi (mevcut gorsel backend'de korunur), null = "Görseli
  // Kaldır" ile acikca silindi, string = yeni yuklenen dosyanin yolu.
  const [imagePath, setImagePath]         = useState(undefined);
  const [uploading, setUploading]         = useState(false);
  const [lightboxUrl, setLightboxUrl]     = useState(null);
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('announcements', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  const fetchAnnouncements = async (page = 1, searchText = debouncedSearch, branch = branchFilter, isActive = activeFilter, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const res = await getAnnouncements({ page, per_page: perPage, search: searchText, branch_id: branch, is_active: isActive, sort_by: sortBy || undefined, sort_direction: sortDir || undefined });
      setAnnouncements(res.data.data);
      setPagination({ current: res.data.current_page, pageSize: res.data.per_page, total: res.data.total });
    } catch {
      message.error('Duyurular yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await getBranches({ per_page: 100 });
      setBranches(res.data.data);
    } catch {}
  };

  useEffect(() => { fetchAnnouncements(1, debouncedSearch, branchFilter, activeFilter); }, [debouncedSearch]);
  useEffect(() => { if (isManager) fetchBranches(); }, []);

  const handleImageUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('image', file);
    setUploading(true);
    try {
      const res = await api.post('/announcements/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(res.data.url);
      setImagePath(res.data.path);
      onSuccess(res.data);
    } catch {
      message.error('Görsel yüklenemedi.');
      onError(new Error('upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => {
    setEditingAnnouncement(null);
    form.resetFields();
    setBodyHtml('');
    setImageUrl('');
    setImagePath(undefined);
    setModalOpen(true);
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    form.setFieldsValue({ title: announcement.title, branch_id: announcement.branch_id, is_active: announcement.is_active });
    setBodyHtml(announcement.body || '');
    setImageUrl(announcement.image_url || '');
    setImagePath(undefined);
    setModalOpen(true);
  };

  const handleSubmit = async (values) => {
    if (!bodyHtml || bodyHtml === '<p><br></p>') {
      message.error('İçerik giriniz.');
      return;
    }
    try {
      const payload = { ...values, body: bodyHtml };
      // imagePath undefined -> kullanici gorseli hic degistirmedi, mevcut
      // gorsel backend'de oldugu gibi kalsin (payload'a hic eklenmiyor).
      if (imagePath !== undefined) payload.image_path = imagePath;
      if (editingAnnouncement) {
        await updateAnnouncement(editingAnnouncement.id, payload);
        message.success('Duyuru güncellendi.');
      } else {
        await createAnnouncement(payload);
        message.success('Duyuru yayınlandı.');
      }
      setModalOpen(false);
      form.resetFields();
      setBodyHtml('');
      setImageUrl('');
      setImagePath(undefined);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (err) {
      message.error(apiErrorMessage(err, 'İşlem başarısız.'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAnnouncement(id);
      message.success('Duyuru silindi.');
      fetchAnnouncements();
    } catch {
      message.error('Duyuru silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      sortOrder: sortField === 'title' ? sortOrder : null,
    },
    {
      title: 'Şube',
      dataIndex: 'branch_id',
      key: 'branch_id',
      render: (branch_id) => {
        if (!branch_id) return <Tag color="purple">Tüm Şubeler</Tag>;
        const branch = branches.find(b => b.id === branch_id);
        return branch ? branch.name : '—';
      },
    },
    {
      title: 'Tarih',
      dataIndex: 'published_at',
      key: 'published_at',
      sorter: true,
      sortOrder: sortField === 'published_at' ? sortOrder : null,
      render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Ekleyen',
      dataIndex: 'created_by',
      key: 'created_by',
      render: (v) => v ? (
        isManager ? (
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${v.id}`)}>
            {v.name} {v.surname}
          </Button>
        ) : `${v.name} ${v.surname}`
      ) : '—',
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      sortOrder: sortField === 'is_active' ? sortOrder : null,
      render: (is_active, record) => (
        isManager ? (
          <Switch
            checked={is_active}
            checkedChildren="Aktif"
            unCheckedChildren="Pasif"
            style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
            onChange={async (checked) => {
              try {
                await updateAnnouncement(record.id, { is_active: checked });
                message.success('Durum güncellendi.');
                setAnnouncements(prev => prev.map(a => a.id === record.id ? { ...a, is_active: checked } : a));
              } catch {
                message.error('Durum güncellenemedi.');
              }
            }}
          />
        ) : (
          <Tag color={is_active ? 'green' : 'default'}>{is_active ? 'Aktif' : 'Pasif'}</Tag>
        )
      ),
    },
    ...(isManager ? [{
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>Düzenle</Button>
          <Popconfirm
            title="Duyuruyu silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Duyurular</h2>
        {isManager && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Yeni Duyuru
          </Button>
        )}
      </div>

      <Card>
        <div className="flex gap-3 mb-4 flex-wrap">
          <Input
            placeholder="Başlık veya içerik ara..."
            allowClear
            style={{ width: 300 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isManager && (
            <Select placeholder="Şube filtrele" allowClear style={{ width: 180 }}
              onChange={(value) => { setBranchFilter(value); fetchAnnouncements(1, debouncedSearch, value, activeFilter); }}>
              {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
            </Select>
          )}
          <Select placeholder="Durum filtrele" allowClear style={{ width: 130 }}
            onChange={(value) => { setActiveFilter(value); fetchAnnouncements(1, debouncedSearch, branchFilter, value); }}>
            <Option value="1">Aktif</Option>
            <Option value="0">Pasif</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={announcements}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchAnnouncements(paginationInfo.current, debouncedSearch, branchFilter, activeFilter, paginationInfo.pageSize, sField, sDir);
          }}
          pagination={{ ...pagination, showSizeChanger: true, pageSizeOptions: ['15', '30', '50', '100'], showTotal: (total) => `Toplam ${total} duyuru` }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '8px 16px' }}>
                {record.image_url && (
                  <img
                    src={record.image_url}
                    alt=""
                    style={{ maxHeight: 240, borderRadius: 8, marginBottom: 12, display: 'block', cursor: 'zoom-in' }}
                    onClick={() => setLightboxUrl(record.image_url)}
                  />
                )}
                <div
                  className="ql-editor prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(record.body) }}
                />
              </div>
            ),
          }}
        />
      </Card>

      {/* Oluştur / Düzenle Modal */}
      <Modal
        title={editingAnnouncement ? 'Duyuru Düzenle' : 'Yeni Duyuru'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingAnnouncement(null); form.resetFields(); setBodyHtml(''); setImageUrl(''); setImagePath(undefined); }}
        footer={null}
        width={780}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Başlık" rules={[{ required: true, message: 'Başlık giriniz.' }]}>
            <Input placeholder="Duyuru başlığı" maxLength={255} />
          </Form.Item>

          <Form.Item label="Görsel (İsteğe Bağlı)">
            {imageUrl ? (
              <div className="relative inline-block">
                <img src={imageUrl} alt="duyuru görseli" style={{ maxHeight: 180, maxWidth: '100%', borderRadius: 8, display: 'block' }} />
                <Button
                  danger
                  size="small"
                  icon={<DeleteFilled />}
                  style={{ marginTop: 8 }}
                  onClick={() => { setImageUrl(''); setImagePath(null); }}
                >
                  Görseli Kaldır
                </Button>
              </div>
            ) : (
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={handleImageUpload}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                  Görsel Yükle
                </Button>
              </Upload>
            )}
          </Form.Item>

          <Form.Item label="İçerik" required>
            <ReactQuill
              theme="snow"
              value={bodyHtml}
              onChange={setBodyHtml}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              style={{ minHeight: 200 }}
              placeholder="Duyuru içeriğini yazın..."
            />
          </Form.Item>

          <Form.Item name="branch_id" label="Şube">
            <Select placeholder="Şube seçiniz (boş bırakılırsa tüm şubelere gönderilir)" allowClear>
              {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
            </Select>
          </Form.Item>

          {editingAnnouncement && (
            <Form.Item name="is_active" label="Durum" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}

          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); setBodyHtml(''); setImageUrl(''); setImagePath(undefined); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingAnnouncement ? 'Güncelle' : 'Yayınla'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Resim Lightbox */}
      <Modal
        open={!!lightboxUrl}
        onCancel={() => setLightboxUrl(null)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 1000, top: 20 }}
        styles={{ body: { padding: 0, textAlign: 'center', background: '#000' } }}
        destroyOnClose
      >
        {lightboxUrl && (
          <img
            src={lightboxUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        )}
      </Modal>
    </div>
  );
};

export default Announcements;
